import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { ReferralSource } from 'models/ReferralSource';
import { destroyReferralSource } from 'services/api/referralSources';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type DeleteReferralSourceModalProps = {
  referralSource: ReferralSource;
  clientId?: string;
  onClose: () => void;
};

export default function DeleteReferralSourceModal({
  referralSource,
  clientId,
  onClose,
}: DeleteReferralSourceModalProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  // `onSuccess` on the hook's options rather than passed per-call to `mutate`. React Query drops
  // per-call callbacks when the component unmounts, and StandardModal's header × stays live while
  // the delete is in flight (only Cancel is disabled) — so closing the modal mid-request left the
  // row deleted on the server with no cache invalidation and no toast, and the list still showing
  // it until something else refetched.
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyReferralSource,
    onSuccess: () => {
      invalidateQueries({ referralSources: true });
      addToast({ text: `${referralSource.name} removed from your referral sources` });
      onClose();
    },
  });

  const handleDelete = () => mutate({ id: referralSource.id, clientId });

  return (
    <StandardModal title='Delete Referral Source?' colorTheme={ColorThemeName.PrimaryRed} onCancel={onClose}>
      <StandardModal.RecordName>{referralSource.name}</StandardModal.RecordName>
      {/* Says what actually happens, which is not a deletion. The last sentence is the part that is
          easy to get wrong: a COMPLETED referral keeps its referral source, because a finished run
          is never re-evaluated, but one still in progress is re-reconciled on its next pass, loses
          the tag and asks again — see ReferralSources::Destroy for the mechanism. Promising that
          everything keeps reporting would have been untrue for exactly the referrals a coordinator
          is still working on. */}
      <Content>
        This referral source will no longer be offered when creating a referral, and new referrals naming it will not be
        matched to it automatically. Referrals you have already completed keep reporting under it. Any referral still in
        progress will ask for its referral source again.
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant='danger'
          onClick={handleDelete}
          loading={isPending}
          disabled={isPending}
          data-testid='confirm-delete-referral-source'>
          Delete Referral Source
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled(BodySmall)`
  display: block;
  margin-bottom: 40px;
`;
